import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { DISCLAIMER_TEXT } from "@/lib/crisisResources";
import { LockedOverlay, LockedBadge } from "@/components/LockedOverlay";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { JourneyMap } from "@/components/JourneyMap";
import { SimpleStreakDisplay } from "@/components/SimpleStreakDisplay";
import ShopFooter from "@/components/ShopFooter";
import axios from "axios";
import { toast } from "sonner";
import { 
  User, 
  Mail, 
  Save, 
  LogOut, 
  Flame, 
  Target,
  Calendar,
  BookOpen,
  TrendingUp,
  Settings,
  ChevronRight,
  Award,
  Zap,
  FileText,
  HelpCircle,
  Bell,
  ShoppingBag,
  ExternalLink,
  Trophy,
  Star,
  Lock,
  Crown,
  Bug,
  Sparkles,
  ArrowLeft,
  Trash2,
  Download,
  Loader2
} from "lucide-react";
import { DisclaimerFull, TermsSummary } from "@/components/Disclaimer";
import { NotificationSettings } from "@/components/NotificationSettings";
import { Badge, BADGES, StreakDisplay, BadgeUnlockCelebration } from "@/components/Badges";
import { ShareButton } from "@/components/ShareFeatures";
import { SubscriptionManagement } from "@/components/SubscriptionManagement";
import { BlockedUsersManager } from "@/components/BlockedUsersManager";

const TRUENORTH_STORE_URL = "https://truenorth-zone.com/collections/zenzone-serenity-wake-up-stay-whole";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { subscription } = useSubscription();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Subscription access checks - pass user for admin bypass
  const canAccessBadges = hasAccess(subscription.type, 'quarterly', user);
  const canAccessPathfinder = hasAccess(subscription.type, 'monthly', user);  // PathFinder is monthly+ feature
  const [firstName, setFirstName] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+27");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newBadge, setNewBadge] = useState(null);
  
  // Export My Data state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateRange, setExportDateRange] = useState("all_time");
  const [exportCustomStart, setExportCustomStart] = useState("");
  const [exportCustomEnd, setExportCustomEnd] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportCheck, setExportCheck] = useState(null);

  // Memoize filtered badges to avoid recalculation on every render
  const earnedBadgesPreview = useMemo(() => {
    if (!gamification?.badges) return [];
    return gamification.badges
      .filter(b => b.earned)
      .slice(0, 6);
  }, [gamification]);

  // Memoize streak data for ShareButton to prevent unnecessary re-renders
  const streakShareData = useMemo(() => ({ days: stats?.streak || 0 }), [stats?.streak]);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, dashboardRes, gamificationRes, trialRes] = await Promise.all([
        axios.get(`${API}/profile`, { withCredentials: true }),
        axios.get(`${API}/dashboard`, { withCredentials: true }),
        axios.get(`${API}/gamification/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/trial/status`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setProfile(profileRes.data);
      setFirstName(profileRes.data?.first_name || profileRes.data?.name || "");
      setPhoneCountryCode(profileRes.data?.phone_country_code || "+27");
      setPhoneNumber(profileRes.data?.phone_number || "");
      
      setStats({
        streak: dashboardRes.data.streak || 0,
        lessonsCompleted: dashboardRes.data.lessons_completed || 0,
        journalEntries: dashboardRes.data.journal_entries || 0,
        overallProgress: dashboardRes.data.overall_progress || 0
      });
      
      if (gamificationRes.data) {
        setGamification(gamificationRes.data);
      }
      
      if (trialRes.data) {
        setTrialStatus(trialRes.data);
      }
    } catch (error) {
      console.error("Profile error:", error);
      // Only show error toast for genuine failures, not initial auth issues
      if (error.response?.status !== 401) {
        toast.error("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        first_name: firstName,
        phone_country_code: phoneCountryCode,
        phone_number: phoneNumber || null,
      }, { withCredentials: true });
      toast.success("Profile updated");
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Export My Data functions
  const checkExportData = async () => {
    setExportLoading(true);
    setExportCheck(null);
    try {
      const payload = {
        date_range: exportDateRange,
        ...(exportDateRange === "custom" && {
          start_date: exportCustomStart,
          end_date: exportCustomEnd
        })
      };
      const response = await axios.post(`${API}/user/export-data/check`, payload, { 
        withCredentials: true 
      });
      setExportCheck(response.data);
    } catch (error) {
      console.error("Export check error:", error);
      if (error.response?.status === 429) {
        setExportCheck({
          can_export: false,
          rate_limited: true,
          message: error.response.data?.detail || "Rate limited. Please try again later."
        });
      } else {
        toast.error(error.response?.data?.detail || "Failed to check export data");
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportDownload = async () => {
    setExportLoading(true);
    try {
      const payload = {
        date_range: exportDateRange,
        ...(exportDateRange === "custom" && {
          start_date: exportCustomStart,
          end_date: exportCustomEnd
        })
      };
      
      const response = await axios.post(`${API}/user/export-data`, payload, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'compass-export.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
      setExportDialogOpen(false);
      setExportCheck(null);
    } catch (error) {
      console.error("Export download error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limited. You can export once per hour.");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "No data to export in selected range.");
      } else {
        toast.error("Failed to download export");
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Reset export state when dialog closes
  const handleExportDialogChange = (open) => {
    setExportDialogOpen(open);
    if (!open) {
      setExportCheck(null);
      setExportDateRange("all_time");
      setExportCustomStart("");
      setExportCustomEnd("");
    }
  };

  const getStreakMessage = (streak) => {
    if (streak === 0) return "Start your journey today";
    if (streak < 7) return "Building momentum";
    if (streak < 30) return "Great consistency!";
    if (streak < 90) return "Impressive dedication";
    return "Transformation in progress";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-28">
        <div className="px-4 pt-6">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted/20 rounded-2xl" />
            <div className="h-24 bg-muted/20 rounded-xl" />
            <div className="h-24 bg-muted/20 rounded-xl" />
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 backdrop-blur-lg border-b border-black/5"
        style={{
          backgroundImage: `linear-gradient(rgba(11,40,33,0.85), rgba(11,40,33,0.9)), url('https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/dofbyzba_TNZ%20Favicon.png" 
                alt="TrueNorth Zone"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-base font-heading text-[#a69373]">
                {user?.name || firstName || "Compass User"}
              </h1>
              <p className="text-xs text-[#a69373]/60 font-semibold">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Bell className="w-4 h-4 text-[#a69373]" />
            </button>
            <a 
              href="https://www.truenorth-zone.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-sm text-[#a69373] text-sm font-semibold hover:bg-black/40 transition-colors"
              style={{ zIndex: 9999, position: 'relative', pointerEvents: 'all' }}
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </a>
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-[#7b9e7b] flex items-center justify-center">
                  <Settings className="w-4 h-4 text-white" />
                </button>
              </DialogTrigger>
            <DialogContent className="bg-[#f5f9f5] border-[#c5d5c5]">
              <DialogHeader>
                <DialogTitle className="text-[#1a1a1a]">Edit Profile</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-[#1a1a1a]">Name</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bg-white border-[#c5d5c5] text-[#1a1a1a]"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1a1a1a]">Email</Label>
                  <div className="p-3 rounded-lg bg-[#e0ebe0] text-[#1a1a1a]/70">
                    {user?.email}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1a1a1a]">Phone Number (optional)</Label>
                  <div className="flex gap-2">
                    <select
                      value={phoneCountryCode}
                      onChange={(e) => setPhoneCountryCode(e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg bg-white border border-[#c5d5c5] text-[#1a1a1a] text-sm"
                      data-testid="phone-country-code"
                    >
                      <option value="+27">+27 (ZA)</option>
                      <option value="+1">+1 (US)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+61">+61 (AU)</option>
                      <option value="+91">+91 (IN)</option>
                      <option value="+49">+49 (DE)</option>
                      <option value="+33">+33 (FR)</option>
                      <option value="+971">+971 (AE)</option>
                      <option value="+65">+65 (SG)</option>
                      <option value="+81">+81 (JP)</option>
                    </select>
                    <Input
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="Phone number"
                      className="flex-1 bg-white border-[#c5d5c5] text-[#1a1a1a]"
                      data-testid="phone-number-input"
                    />
                  </div>
                  <p className="text-xs text-[#1a1a1a]/50">For daily signal notifications via WhatsApp/SMS</p>
                </div>
                <Button 
                  className="w-full btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      <main className="px-4 pt-4 space-y-4">
        {/* Badges Section - Gated to Quarterly+ (Moved to top) */}
        {gamification && (
          <div className="relative">
            <Card className="glass-card overflow-hidden" data-testid="badges-section">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#f0e0d8] flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-[#a89060]" />
                    </div>
                    <div>
                      <h3 className="text-[#a69373] font-medium">Badges & Achievements</h3>
                      <p className="text-[#a69373]/50 text-sm">
                        {canAccessBadges 
                          ? `${gamification.stats.badges_earned} of ${gamification.stats.badges_total} earned`
                          : "Available on Quarterly and above"
                        }
                      </p>
                    </div>
                  </div>
                  {!canAccessBadges && (
                    <Lock className="w-5 h-5 text-white/30" />
                  )}
                </div>
                
                {/* Earned badges preview */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {canAccessBadges ? (
                    <>
                      {earnedBadgesPreview.map(badge => {
                          const badgeDef = BADGES[badge.badge_id];
                          if (!badgeDef) return null;
                          const isPathfinder = badge.badge_id === 'pathfinder';
                          if (isPathfinder && !canAccessPathfinder) {
                            return (
                              <div key={badge.badge_id} className="relative">
                                <Badge badge={badgeDef} earned={false} size="sm" />
                                <LockedBadge tooltip="Available on Annual plan" />
                              </div>
                            );
                          }
                          return (
                            <Badge 
                              key={badge.badge_id} 
                              badge={badgeDef} 
                              earned={true} 
                              size="sm" 
                            />
                          );
                        })}
                      {gamification.stats.badges_earned === 0 && (
                        <p className="text-[#a69373]/40 text-sm italic">Complete check-ins to earn your first badge!</p>
                      )}
                    </>
                  ) : (
                    Object.values(BADGES).slice(0, 6).map(badgeDef => (
                      <div key={badgeDef.id} className="relative opacity-40">
                        <Badge badge={badgeDef} earned={false} size="sm" />
                        <LockedBadge tooltip="Available on Quarterly and above" />
                      </div>
                    ))
                  )}
                </div>
                
                {/* Streak milestones */}
                <div className="bg-[#f5f9f5] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-medium text-[#a69373]">Streak Progress</span>
                    </div>
                    <span className="text-sm text-[#a69373]">
                      {gamification.streak.current_streak} days
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    {[3, 7, 14, 30, 90].map(milestone => (
                      <div 
                        key={milestone}
                        className={`flex-1 h-2 rounded-full transition-colors ${
                          gamification.streak.current_streak >= milestone 
                            ? "bg-orange-400" 
                            : "bg-gray-200"
                        }`}
                        title={`${milestone} day streak`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between text-[10px] text-[#a69373]/40 mt-1.5">
                    <span>3d</span>
                    <span>7d</span>
                    <span>14d</span>
                    <span>30d</span>
                    <span>90d</span>
                  </div>
                  {gamification.streak.longest_streak > 0 && (
                    <p className="text-xs text-[#a69373]/50 mt-2">
                      Best streak: <span className="font-medium text-[#a69373]">{gamification.streak.longest_streak} days</span>
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            {!canAccessBadges && (
              <LockedOverlay 
                requiredTier="quarterly" 
                message="Available on Quarterly and above"
                ctaText="Upgrade to Quarterly"
              />
            )}
          </div>
        )}

        {/* Streak Card */}
        <Card className="glass-card stat-card overflow-hidden" data-testid="streak-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="stat-icon w-16 h-16 rounded-2xl bg-gradient-to-br from-[#f0e0d8] to-[#e8d0c8] flex items-center justify-center">
                <Flame className="w-8 h-8 text-[#c08050]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: '#a69373' }}>Current Streak</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-heading" style={{ color: '#a69373' }}>{stats?.streak || 0}</span>
                  <span style={{ color: 'rgba(166, 147, 115, 0.6)' }}>days</span>
                </div>
                <p className="text-[#5a8a5a] text-sm">{getStreakMessage(stats?.streak || 0)}</p>
              </div>
              <ShareButton 
                type="streak" 
                data={streakShareData}
                variant="outline"
                size="sm"
                className="bg-white/80 hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/20"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="glass-card stat-card overflow-hidden" data-testid="stat-lessons">
            <CardContent className="p-4 text-center">
              <div className="stat-icon w-10 h-10 rounded-xl bg-[#c8d8e8] flex items-center justify-center mx-auto mb-2">
                <BookOpen className="w-5 h-5 text-[#5a7a9a]" />
              </div>
              <p className="text-2xl font-heading text-white">{stats?.lessonsCompleted || 0}</p>
              <p className="text-white/50 text-xs">Lessons</p>
            </CardContent>
          </Card>
          
          <Card className="glass-card stat-card overflow-hidden" data-testid="stat-journal">
            <CardContent className="p-4 text-center">
              <div className="stat-icon w-10 h-10 rounded-xl bg-[#d8d0e8] flex items-center justify-center mx-auto mb-2">
                <Calendar className="w-5 h-5 text-[#7a6a9a]" />
              </div>
              <p className="text-2xl font-heading text-white">{stats?.journalEntries || 0}</p>
              <p className="text-white/50 text-xs">Journal</p>
            </CardContent>
          </Card>
          
          <Card className="glass-card stat-card overflow-hidden" data-testid="stat-progress">
            <CardContent className="p-4 text-center">
              <div className="stat-icon w-10 h-10 rounded-xl bg-[#d4e4d4] flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="w-5 h-5 text-[#5a8a5a]" />
              </div>
              <p className="text-2xl font-heading text-white">{stats?.overallProgress || 0}%</p>
              <p className="text-white/50 text-xs">Progress</p>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Status Card */}
        <Card className="glass-card overflow-hidden" data-testid="subscription-status-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  subscription.type === 'annual' 
                    ? 'bg-gradient-to-br from-[#a69373] to-[#a88b50]' 
                    : subscription.type === 'quarterly'
                      ? 'bg-gradient-to-br from-[#7b9e7b] to-[#5a8a5a]'
                      : subscription.type === 'monthly'
                        ? 'bg-gradient-to-br from-[#5a7a9a] to-[#4a6a8a]'
                        : 'bg-white/10'
                }`}>
                  <Crown className={`w-7 h-7 ${
                    subscription.type === 'annual' ? 'text-[#0B2821]' : 'text-white'
                  }`} />
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'rgba(166, 147, 115, 0.6)' }}>Your Plan</p>
                  <p className="text-xl font-heading font-bold capitalize" style={{ color: '#a69373' }}>
                    {subscription.type === 'none' ? 'Free Plan' : subscription.type}
                  </p>
                  {/* Trial countdown - non-intrusive, small text */}
                  {trialStatus?.is_active && trialStatus?.days_remaining > 0 && (
                    <p className="text-[#a69373]/80 text-xs mt-1">
                      Full access · {trialStatus.days_remaining} {trialStatus.days_remaining === 1 ? 'day' : 'days'} left
                    </p>
                  )}
                  {/* Only show days remaining for paid plans (non-trial) */}
                  {!trialStatus?.is_active && subscription.type !== 'none' && subscription.isActive && subscription.daysRemaining > 0 && (
                    <p className="text-[#a69373] text-sm">
                      {subscription.daysRemaining} days remaining
                    </p>
                  )}
                  {subscription.type !== 'none' && !subscription.isActive && !trialStatus?.is_active && (
                    <p className="text-red-400 text-sm">Expired</p>
                  )}
                </div>
              </div>
              
              {/* Show Upgrade CTA or PathFinder eligible badge */}
              {subscription.type === 'annual' && subscription.isActive ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#a69373]/20 border border-[#a69373]/30">
                  <Sparkles className="w-4 h-4 text-[#a69373]" />
                  <span className="text-sm font-medium text-[#a69373]">PathFinder</span>
                </div>
              ) : subscription.type === 'none' ? (
                <a
                  href={MEMBERSHIP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold text-sm transition-colors"
                  data-testid="upgrade-subscription-btn"
                >
                  <Crown className="w-4 h-4" />
                  Unlock full experience
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <a
                  href={MEMBERSHIP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-medium text-sm transition-colors"
                  data-testid="upgrade-subscription-btn"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            
            {/* Feature access summary */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs uppercase tracking-wider mb-2 font-bold" style={{ color: '#a69373' }}>Access Level</p>
              <div className="flex flex-wrap gap-2">
                {/* Free tier always has basic features */}
                <span className="px-2 py-1 rounded text-xs bg-[#5a8a5a]/20 text-[#7b9e7b]">
                  ✓ Check-in & Journal
                </span>
                <span className={`px-2 py-1 rounded text-xs ${
                  hasAccess(subscription.type, 'monthly', user) ? 'bg-[#5a8a5a]/20 text-[#7b9e7b]' : 'bg-white/5 text-white/30'
                }`}>
                  {hasAccess(subscription.type, 'monthly', user) ? '✓' : '✗'} Full Wellness
                </span>
                <span className={`px-2 py-1 rounded text-xs ${
                  hasAccess(subscription.type, 'quarterly', user) ? 'bg-[#5a8a5a]/20 text-[#7b9e7b]' : 'bg-white/5 text-white/30'
                }`}>
                  {hasAccess(subscription.type, 'quarterly', user) ? '✓' : '✗'} Badges
                </span>
                <span className={`px-2 py-1 rounded text-xs ${
                  hasAccess(subscription.type, 'annual', user) ? 'bg-[#a69373]/20 text-[#a69373]' : 'bg-white/5 text-white/30'
                }`}>
                  {hasAccess(subscription.type, 'annual', user) ? '✓' : '✗'} Quests
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Management Section */}
        <section className="mt-4" data-testid="subscription-management-section">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: '#a69373' }}>Manage Subscription</h3>
          <SubscriptionManagement />
        </section>

        {/* Simple Streak Display - replaces complex Journey Map */}
        <section className="mt-4" data-testid="profile-streak-section">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: '#a69373' }}>Your Journey</h3>
          <SimpleStreakDisplay streak={stats?.streak || 0} showHistory={true} />
        </section>

        {/* Achievement Card */}
        {/* Quick Actions */}
        <div className="space-y-2">
          <h3 className="text-sm text-white/60 px-1">Quick Actions</h3>
          
          {/* PathFinder - Featured */}
          <Card 
            className="glass-card clickable-card overflow-hidden cursor-pointer border border-[#BF9B5A]/30"
            onClick={() => navigate("/pathfinder")}
            data-testid="pathfinder-link"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#0f1e1a] border border-[#BF9B5A] flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                  <path d="M24 38C24 38 16 32 16 24C16 18 20 14 24 10C28 14 32 18 32 24C32 32 24 38 24 38Z" fill="#8a4a10"/>
                  <path d="M24 34C24 34 20 30 20 26C20 22 22 18 24 15C26 18 28 22 28 26C28 30 24 34 24 34Z" fill="#e09030"/>
                  <path d="M24 30C24 30 22 28 22 26C22 24 23 22 24 20C25 22 26 24 26 26C26 28 24 30 24 30Z" fill="#ffd870"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-medium" style={{ color: '#BF9B5A' }}>PathFinder</h3>
                <p className="text-white/50 text-sm">AI Companion</p>
              </div>
              <ChevronRight className="card-arrow w-5 h-5 text-[#BF9B5A]/50" />
            </CardContent>
          </Card>
          
          <Card 
            className="glass-card clickable-card overflow-hidden cursor-pointer"
            onClick={() => navigate("/compass")}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#d4e4d4] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#5a8a5a]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">Daily Compass</h3>
                <p className="text-white/50 text-sm">Complete today's action</p>
              </div>
              <ChevronRight className="card-arrow w-5 h-5 text-white/30" />
            </CardContent>
          </Card>

          <Card 
            className="glass-card clickable-card overflow-hidden cursor-pointer"
            onClick={() => navigate("/weekly-reset")}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#c8d8e8] flex items-center justify-center">
                <Target className="w-5 h-5 text-[#5a7a9a]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">Weekly Reset</h3>
                <p className="text-white/50 text-sm">Review and recommit</p>
              </div>
              <ChevronRight className="card-arrow w-5 h-5 text-white/30" />
            </CardContent>
          </Card>

          <Card 
            className="glass-card clickable-card overflow-hidden cursor-pointer"
            onClick={() => navigate("/community")}
            data-testid="community-link"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#e0d8e8] flex items-center justify-center">
                <Award className="w-5 h-5 text-[#7a6090]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">Community</h3>
                <p className="text-white/50 text-sm">Share achievements & connect</p>
              </div>
              <ChevronRight className="card-arrow w-5 h-5 text-white/30" />
            </CardContent>
          </Card>

          <Card 
            className="glass-card clickable-card overflow-hidden cursor-pointer"
            onClick={() => navigate("/faq")}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#d0dce8] flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-[#5a7090]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">FAQ</h3>
                <p className="text-white/50 text-sm">Common questions</p>
              </div>
              <ChevronRight className="card-arrow w-5 h-5 text-white/30" />
            </CardContent>
          </Card>
        </div>

        {/* Notification Settings */}
        <div className="space-y-2 mt-4">
          <h3 className="text-sm text-white/60 px-1">Notifications</h3>
          <NotificationSettings />
        </div>

        {/* Blocked Users */}
        <div className="space-y-2 mt-4">
          <h3 className="text-sm text-white/60 px-1">Privacy</h3>
          <BlockedUsersManager />
        </div>

        {/* Export My Data */}
        <div className="space-y-2 mt-4">
          <h3 className="text-sm text-white/60 px-1">Your Data</h3>
          
          <Dialog open={exportDialogOpen} onOpenChange={handleExportDialogChange}>
            <DialogTrigger asChild>
              <Card className="glass-card clickable-card overflow-hidden cursor-pointer">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#c8d8e8] flex items-center justify-center">
                    <Download className="w-5 h-5 text-[#4a6a8a]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium">Export My Data</h3>
                    <p className="text-white/50 text-sm">Download your journals & check-ins</p>
                  </div>
                  <ChevronRight className="card-arrow w-5 h-5 text-white/30" />
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-[#0f3129] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Export My Data</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <p className="text-white/70 text-sm">
                  Download a PDF containing your check-ins, journal entries, and quest progress.
                </p>
                
                {/* Date Range Selection */}
                <div className="space-y-3">
                  <label className="text-white text-sm font-medium">Date Range</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: "all_time", label: "All Time" },
                      { value: "last_12_months", label: "Last 12 Months" },
                      { value: "last_3_months", label: "Last 3 Months" },
                      { value: "custom", label: "Custom Range" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setExportDateRange(option.value);
                          setExportCheck(null);
                        }}
                        className={`p-3 rounded-xl text-sm font-medium transition-colors ${
                          exportDateRange === option.value
                            ? "bg-[#a69373] text-white"
                            : "bg-white/10 text-white/70 hover:bg-white/20"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Custom Date Pickers */}
                {exportDateRange === "custom" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white text-sm">Start Date</Label>
                      <Input
                        type="date"
                        value={exportCustomStart}
                        onChange={(e) => {
                          setExportCustomStart(e.target.value);
                          setExportCheck(null);
                        }}
                        className="bg-white/10 border-white/20 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-sm">End Date</Label>
                      <Input
                        type="date"
                        value={exportCustomEnd}
                        onChange={(e) => {
                          setExportCustomEnd(e.target.value);
                          setExportCheck(null);
                        }}
                        className="bg-white/10 border-white/20 text-white mt-1"
                      />
                    </div>
                  </div>
                )}
                
                {/* Check Data Button */}
                {!exportCheck && (
                  <Button
                    onClick={checkExportData}
                    disabled={exportLoading || (exportDateRange === "custom" && (!exportCustomStart || !exportCustomEnd))}
                    className="w-full bg-white/10 hover:bg-white/20 text-white"
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      "Check Available Data"
                    )}
                  </Button>
                )}
                
                {/* Export Check Result */}
                {exportCheck && (
                  <div className={`p-4 rounded-xl ${
                    exportCheck.can_export 
                      ? "bg-[#1a4a3f]/30 border border-[#1a4a3f]/50" 
                      : "bg-[#a69373]/10 border border-[#a69373]/30"
                  }`}>
                    {exportCheck.rate_limited ? (
                      <p className="text-[#a69373] text-sm">{exportCheck.message}</p>
                    ) : exportCheck.no_data ? (
                      <p className="text-[#a69373] text-sm">{exportCheck.message}</p>
                    ) : (
                      <>
                        <p className="text-[#c4b395] text-sm font-medium mb-2">Ready to export:</p>
                        <ul className="text-white/70 text-sm space-y-1">
                          <li>• {exportCheck.checkin_count?.toLocaleString()} check-ins</li>
                          <li>• {exportCheck.journal_count?.toLocaleString()} journal entries</li>
                          <li>• {exportCheck.quest_count?.toLocaleString()} quests enrolled</li>
                        </ul>
                        {exportCheck.warning && (
                          <p className="text-[#a69373] text-xs mt-2">{exportCheck.warning}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {/* Download Button */}
                {exportCheck?.can_export && (
                  <Button
                    onClick={handleExportDownload}
                    disabled={exportLoading}
                    className="w-full bg-[#a69373] hover:bg-[#8b7a5e] text-white"
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                )}
                
                <p className="text-white/40 text-xs text-center">
                  You can export once per hour. PathFinder conversations are not included.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Terms & Disclaimer */}
        <div className="space-y-2 mt-4">
          <h3 className="text-sm text-white/60 px-1">Legal</h3>
          
          <Card className="glass-card overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#e8dcc8] flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#a89060]" />
                </div>
                <h3 className="text-white font-medium">Terms & Disclaimer</h3>
              </div>
              <TermsSummary />
              <div className="mt-4">
                <DisclaimerFull />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report a Bug */}
        <Card className="glass-card overflow-hidden mt-6">
          <CardContent className="p-2">
            <a 
              href="mailto:info@truenorth-zone.com?subject=Bug%20Report%20%E2%80%93%20Compass%20App&body=Please%20describe%20the%20bug%20you%20encountered%3A%0A%0ADevice%3A%20%0AScreen%3A%20%0AWhat%20happened%3A%20"
              className="w-full flex items-center justify-start px-4 py-2 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white rounded-md transition-colors"
              data-testid="report-bug-btn"
            >
              <Bug className="w-4 h-4 mr-3" />
              Report a Bug
            </a>
          </CardContent>
        </Card>

        {/* Sign Out */}
        <Card className="glass-card overflow-hidden mt-6">
          <CardContent className="p-2">
            <Button 
              variant="ghost"
              className="w-full justify-start text-[#a06060] hover:bg-[#e8d0d0] hover:text-[#8a5050]"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="glass-card overflow-hidden mt-2 border-red-200/30">
          <CardContent className="p-2">
            <Button 
              variant="ghost"
              className="w-full justify-start text-red-400/70 hover:bg-red-500/10 hover:text-red-500"
              onClick={() => navigate("/delete-account")}
              data-testid="delete-account-link"
            >
              <Trash2 className="w-4 h-4 mr-3" />
              Delete Account
            </Button>
          </CardContent>
        </Card>

        {/* Privacy Notice Link */}
        <div className="mt-4 text-center">
          <span 
            className="text-xs text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            onClick={() => navigate("/privacy")}
          >
            Read our Privacy Notice
          </span>
        </div>

        {/* Scope Disclaimer */}
        <div className="px-4 py-6 border-t border-white/5">
          <p className="text-xs text-center text-white/40">
            {DISCLAIMER_TEXT}
          </p>
        </div>

        {/* App Version */}
        <p className="text-center text-white/30 text-xs py-4">
          Compass v2.0 · © 2025
        </p>
        
        {/* Shop Footer */}
        <ShopFooter />
      </main>

      <BottomNavigation />
      
      {/* Badge unlock celebration modal */}
      {newBadge && BADGES[newBadge] && (
        <BadgeUnlockCelebration 
          badge={BADGES[newBadge]} 
          onClose={() => setNewBadge(null)} 
        />
      )}
    </div>
  );
}
